import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent,
  type Ref,
} from 'react';
import { useT } from '../../i18n';
import { Checkbox } from '../Checkbox/Checkbox';
import { TextField } from '../Field/Field';
import './signature.css';

/** What the pad hands to `signClaim` / `signForMinor` / `signPart` (contract 1.3 `SignatureRequest`). */
export type SignatureValue =
  { kind: 'drawn'; image: string } | { kind: 'typed'; typedName: string; typedAgreed: true };

export interface SignaturePadHandle {
  clear(): void;
  isEmpty(): boolean;
  /** PNG data URL of the drawn signature at device resolution (null if nothing is drawn). */
  toDataURL(): string | null;
  /** The signature in the mode on screen, or null until it is complete. */
  value(): SignatureValue | null;
}

export interface SignaturePadProps {
  /** Fires true once there is a signature to send (a stroke; or a typed name with its box
   *  ticked), false when there isn't any more. Drives the Sign button. */
  onInkChange?: (hasInk: boolean) => void;
  /** Accessible name of the canvas. */
  label?: string;
  /** Show the "Turning your phone sideways…" hint on narrow portrait screens (S11). */
  rotateHint?: boolean;
  /** Offer "Can't draw? Type your full name instead" (Q46). Default true. */
  allowTyped?: boolean;
  ref?: Ref<SignaturePadHandle>;
}

/** Contract `TypedName`: no markup, no control characters, at most 120 characters. */
const TYPED_BAD = /[<>%\p{C}]/u;
export const TYPED_MAX = 120;

type Point = [number, number];
const INK = '#3B3A40';
const LINE = 2.6;

/**
 * Pointer-event signature canvas (the prototype's sigPad): mouse, touch and pen, DPR-aware.
 * Strokes are kept in CSS pixels and redrawn when the canvas resizes (e.g. the phone is
 * turned sideways), so a size change never loses the signature.
 *
 * Q46 (Anton's interim answer, open for counsel): "Can't draw? Type your full name instead"
 * swaps the canvas for a name field and a required "I agree this typed name is my
 * signature" box — the way in for a keyboard or screen-reader user. The canvas stays
 * mounted (hidden), so going back to drawing keeps what was drawn.
 */
export function SignaturePad({
  onInkChange,
  label,
  rotateHint = true,
  allowTyped = true,
  ref,
}: SignaturePadProps) {
  const t = useT();
  const canvas = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Point[][]>([]);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [typing, setTyping] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const nameId = `signame${useId()}`;
  const switchRef = useRef<HTMLButtonElement>(null);
  const typedRef = useRef({ typing, typedName, agreed });
  useEffect(() => {
    typedRef.current = { typing, typedName, agreed };
  }, [typing, typedName, agreed]);
  /* Mirrors `hasInk` for the event handlers, so the parent is told outside any state updater
     (a parent update from inside one is an update during render). */
  const inked = useRef(false);
  const onInk = useRef(onInkChange);
  useEffect(() => {
    onInk.current = onInkChange;
  }, [onInkChange]);

  const ctx = useCallback(() => canvas.current?.getContext('2d') ?? null, []);

  const setup = useCallback(() => {
    const c = canvas.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(r.width * dpr);
    c.height = Math.round(r.height * dpr);
    const g = ctx();
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.lineWidth = LINE;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.strokeStyle = INK;
    for (const s of strokes.current) {
      if (!s.length) continue;
      g.beginPath();
      g.moveTo(...s[0]!);
      for (const p of s.slice(1)) g.lineTo(...p);
      g.stroke();
    }
  }, [ctx]);

  useEffect(() => {
    setup();
    const c = canvas.current;
    if (!c || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setup());
    ro.observe(c);
    return () => ro.disconnect();
  }, [setup]);

  /* Readiness is what the parent hears about: the drawn ink, or a complete typed signature. */
  const ready = useRef(false);
  const tell = useCallback((now: boolean) => {
    if (ready.current === now) return;
    ready.current = now;
    onInk.current?.(now);
  }, []);
  const typedOk = (name: string, ok: boolean) =>
    ok && name.trim() !== '' && !TYPED_BAD.test(name) && name.trim().length <= TYPED_MAX;

  const mark = (ink: boolean) => {
    if (inked.current === ink) return;
    inked.current = ink;
    setHasInk(ink);
    if (!typedRef.current.typing) tell(ink);
  };

  const switchMode = (toTyping: boolean) => {
    setTyping(toTyping);
    tell(toTyping ? typedOk(typedName, agreed) : inked.current);
    /* Focus follows the switch: into the name field, or back to the link that brings it back. */
    requestAnimationFrame(() =>
      toTyping ? document.getElementById(nameId)?.focus() : switchRef.current?.focus(),
    );
  };

  const at = (e: PointerEvent<HTMLCanvasElement>): Point => {
    const r = e.currentTarget.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  const clear = useCallback(() => {
    strokes.current = [];
    setup();
    inked.current = false;
    setHasInk(false);
    if (!typedRef.current.typing) {
      ready.current = false;
      onInk.current?.(false);
    }
  }, [setup]);

  useImperativeHandle(
    ref,
    () => ({
      clear,
      isEmpty: () => strokes.current.length === 0,
      toDataURL: () =>
        strokes.current.length && canvas.current ? canvas.current.toDataURL('image/png') : null,
      value: () => {
        const { typing: typed, typedName: name, agreed: ok } = typedRef.current;
        if (typed) {
          return typedOk(name, ok)
            ? { kind: 'typed', typedName: name.trim().replace(/\s+/g, ' '), typedAgreed: true }
            : null;
        }
        const image = strokes.current.length && canvas.current ? canvas.current.toDataURL('image/png') : null;
        return image ? { kind: 'drawn', image } : null;
      },
    }),
    [clear],
  );

  const badName = TYPED_BAD.test(typedName);

  return (
    <>
      <div className="sigwrap" hidden={typing}>
        <canvas
          ref={canvas}
          className="sig"
          role="img"
          aria-label={label ?? t('common.signature.label')}
          data-testid="signature-canvas"
          onPointerDown={(e) => {
            drawing.current = true;
            e.currentTarget.setPointerCapture?.(e.pointerId);
            const p = at(e);
            strokes.current.push([p]);
            const g = ctx();
            g?.beginPath();
            g?.moveTo(...p);
            // A tap leaves a dot, like a pen would.
            g?.lineTo(p[0] + 0.1, p[1] + 0.1);
            g?.stroke();
            mark(true);
          }}
          onPointerMove={(e) => {
            if (!drawing.current) return;
            const p = at(e);
            strokes.current.at(-1)?.push(p);
            const g = ctx();
            g?.lineTo(...p);
            g?.stroke();
          }}
          onPointerUp={() => (drawing.current = false)}
          onPointerCancel={() => (drawing.current = false)}
          onPointerLeave={() => (drawing.current = false)}
        />
        <span className="sig__hint" hidden={hasInk} aria-hidden="true">
          {t('common.signature.hint')}
        </span>
        <button type="button" className="sig__clear" onClick={clear}>
          {t('common.signature.clear')}
        </button>
      </div>
      {typing && (
        <div className="sigtype">
          <TextField
            id={nameId}
            label={t('common.signature.typedLabel')}
            value={typedName}
            maxLength={TYPED_MAX}
            autoComplete="name"
            autoCapitalize="words"
            spellCheck={false}
            lift={false}
            error={badName ? t('common.signature.typedInvalid') : undefined}
            onChange={(e) => {
              setTypedName(e.target.value);
              tell(typedOk(e.target.value, agreed));
            }}
          />
          {typedName.trim() !== '' && !badName && (
            <p className="sigtype__sample" aria-hidden="true">
              {typedName}
            </p>
          )}
          <Checkbox
            checked={agreed}
            required
            onChange={(ok) => {
              setAgreed(ok);
              tell(typedOk(typedName, ok));
            }}
          >
            {t('common.signature.typedAgree')}
          </Checkbox>
        </div>
      )}
      {rotateHint && !typing && <p className="rotate">{t('common.signature.rotate')}</p>}
      {allowTyped && (
        <button type="button" className="sig__switch" ref={switchRef} onClick={() => switchMode(!typing)}>
          {typing ? t('common.signature.drawInstead') : t('common.signature.typeInstead')}
        </button>
      )}
    </>
  );
}
